import { DownloadIcon, Trash2Icon } from "lucide-react";

import { useI18n } from "~/i18n";
import { cn } from "~/lib/utils";
import { VAULT_ITEM_DRAG_TYPE } from "./VaultFolderCard";
import { Spinner } from "../ui/spinner";
import type { VaultItem } from "../../morrowVault";
import { VaultFileFolderMenu } from "./VaultFileFolderMenu";
import { MorrowPersonAvatar } from "../workspace/MorrowPersonCard";
import {
  formatBytes,
  formatDate,
  iconForMime,
  resolveVaultMimeType,
  typeLabel,
} from "./vaultShared";
import type { CustomVaultFolder } from "./vaultFolders";

const FILE_LIST_COLUMNS =
  "grid-cols-[minmax(0,1.6fr)_minmax(0,0.7fr)_minmax(0,0.55fr)_minmax(0,0.9fr)_auto]";

export function VaultFileList({
  items,
  addedByLabel,
  customFolders,
  itemFolderAssignments,
  deletingIds,
  onOpen,
  onDownload,
  onDelete,
  onAssignFolder,
}: {
  items: readonly VaultItem[];
  addedByLabel: string;
  customFolders: readonly CustomVaultFolder[];
  itemFolderAssignments: Readonly<Record<string, string>>;
  deletingIds: ReadonlySet<string>;
  onOpen: (item: VaultItem) => void;
  onDownload: (item: VaultItem) => void;
  onDelete: (item: VaultItem) => void;
  onAssignFolder: (itemId: string, folderId: string | null) => void;
}) {
  const { t } = useI18n();

  if (items.length === 0) {
    return (
      <div className="rounded-2xl border border-border/60 bg-card px-6 py-10 text-center text-sm text-muted-foreground">
        {t("vault.folderEmpty")}
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border border-border/60 bg-card">
      <div
        className={cn(
          "grid shrink-0 items-center gap-x-4 border-b border-border/60 bg-muted/35 px-4 py-3 text-xs font-medium text-muted-foreground",
          FILE_LIST_COLUMNS,
        )}
      >
        <span className="min-h-5 leading-5">{t("vault.col.name")}</span>
        <span className="hidden min-h-5 leading-5 sm:block">{t("vault.col.type")}</span>
        <span className="hidden min-h-5 leading-5 md:block">{t("vault.col.size")}</span>
        <span className="min-h-5 leading-5">{t("vault.col.addedBy")}</span>
        <span className="sr-only">{t("vault.col.actions")}</span>
      </div>
      <ul className="min-h-0 flex-1 divide-y divide-border/50 overflow-y-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {items.map((item) => {
          const mimeType = resolveVaultMimeType(item);
          const Icon = iconForMime(mimeType);
          const deleting = deletingIds.has(item.id);
          return (
            <li key={item.id}>
              <div
                // Draggable onto a folder card to file it. The custom type is what lets the card
                // tell this apart from an operating-system file drop on the same element; the
                // overflow menu stays as the keyboard-reachable route.
                draggable
                onDragStart={(event) => {
                  event.dataTransfer.setData(VAULT_ITEM_DRAG_TYPE, item.id);
                  event.dataTransfer.effectAllowed = "move";
                }}
                className={cn(
                  "group grid items-center gap-x-4 px-4 py-3.5",
                  FILE_LIST_COLUMNS,
                  "transition-colors hover:bg-accent/20",
                  deleting && "opacity-60",
                )}
              >
                <button
                  type="button"
                  onClick={() => onOpen(item)}
                  className="flex min-w-0 items-center gap-3 text-left"
                >
                  <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-muted/50 text-muted-foreground">
                    <Icon className="size-4" aria-hidden="true" />
                  </span>
                  <span className="min-w-0">
                    <span
                      className="block truncate text-sm font-medium leading-5 text-foreground"
                      title={item.name}
                    >
                      {item.name}
                    </span>
                    {item.itemCount > 0 ? (
                      <span className="mt-0.5 block truncate text-xs leading-4 text-emerald-600 dark:text-emerald-400">
                        {t("vault.itemsIndexed", { count: item.itemCount })}
                      </span>
                    ) : null}
                    <span className="mt-0.5 block truncate text-xs leading-4 text-muted-foreground sm:hidden">
                      {typeLabel(mimeType)} · {formatBytes(item.sizeBytes)}
                    </span>
                  </span>
                </button>

                <span className="hidden truncate text-sm leading-5 text-muted-foreground sm:block">
                  {typeLabel(mimeType)}
                </span>

                <span className="hidden text-sm leading-5 text-muted-foreground md:block">
                  {formatBytes(item.sizeBytes)}
                </span>

                <div className="flex min-w-0 items-center gap-2.5">
                  <MorrowPersonAvatar name={addedByLabel} size="xs" />
                  <span
                    className="min-w-0 truncate text-sm leading-5 text-muted-foreground"
                    title={addedByLabel}
                  >
                    {addedByLabel}
                  </span>
                </div>

                <div className="flex items-center justify-end gap-0.5">
                  <VaultFileFolderMenu
                    itemName={item.name}
                    customFolders={customFolders}
                    assignedFolderId={itemFolderAssignments[item.id]}
                    onAssign={(folderId) => onAssignFolder(item.id, folderId)}
                  />
                  <button
                    type="button"
                    aria-label={`${t("vault.download")} ${item.name}`}
                    disabled={deleting}
                    onClick={() => onDownload(item)}
                    className="flex size-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  >
                    <DownloadIcon className="size-4" aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    aria-label={`Delete ${item.name}`}
                    disabled={deleting}
                    onClick={() => onDelete(item)}
                    className="flex size-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-destructive"
                  >
                    {deleting ? (
                      <Spinner className="size-4" />
                    ) : (
                      <Trash2Icon className="size-4" aria-hidden="true" />
                    )}
                  </button>
                </div>
              </div>
              {item.createdAt ? (
                <div className="border-t border-border/40 px-4 py-2 text-xs text-muted-foreground sm:hidden">
                  {formatDate(item.createdAt)}
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
